import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { AuthContext } from "../contexts/AuthContext.jsx";
import axios from "axios";
import Logo from "../components/Logo.jsx";
import { prodApi } from "../data/api.js";
import { input } from "../styles/styles.js";

const loginUrl = prodApi.login;
const registerUrl = prodApi.register;
const userUrl = prodApi.user;

const AuthModal = () => {
  const { loggedIn, setLoggedIn, setIsLoggedIn } = useContext(AuthContext);
  const [firstName, setFirstname] = useState("");
  const [lastName, setLastname] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLogin, setIsLogin] = useState(true); // State to toggle between login and register
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);

  const colourTheme = useTheme();
  const navigate = useNavigate();

  const handleToggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError(""); // Reset any error when switching
    setSuccess("");
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Registration validation
    if (!isLogin) {
      if (
        !firstName ||
        !lastName ||
        !username ||
        !phone ||
        !password ||
        !confirmPassword
      ) {
        setError("FILL IN ALL FIELDS.");
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError("PASSWORDS DO NOT MATCH.");
        setLoading(false);
        return;
      }
    }

    const authUrl = isLogin ? loginUrl : registerUrl;
    const authPayload = isLogin
      ? { username, password }
      : { firstName, lastName, username, phone, password };

    try {
      const response = await axios.post(authUrl, authPayload);

      // Handle registration success
      if (!isLogin) {
        setSuccess("REGISTRATION SUCCESSFUL");
        setTimeout(() => {
          handleToggleAuthMode(); // Switch to login mode
          setSuccess("");
        }, 2000);
      } else {
        // Handle login success actions
        const { token } = response.data;
        localStorage.setItem("token", token);

        const userResponse = await axios.get(userUrl, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: "Include",
        });

        localStorage.setItem("user", JSON.stringify(userResponse.data));
        setIsLoggedIn(userResponse.data);
        setSuccess("WELCOME");
        setTimeout(() => {
          setLoggedIn(true);
          navigate("/");
        }, 1000);
      }
    } catch (error) {
      // Check if the error status is 409 (Email already exists)
      if (error.response && error.response.status === 409) {
        setError("EMAIL ALREADY EXISTS.");
        // Optionally, reset fields for re-entry
        setFirstname("");
        setLastname("");
        setUsername("");
        setPhone("");
        setPassword("");
        setConfirmPassword("");
      } else {
        const message =
          error.response && error.response.status === 401
            ? "INVALID CREDENTIALS."
            : "CONNECTION ERROR. TRY AGAIN LATER.";
        setError(message);
      }
    } finally {
      setLoading(false);
      setTimeout(() => {
        setError("");
        setSuccess("");
      }, 1000);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  if (loggedIn) {
    return null;
  }

  return (
    // Auth Modal Background
    <Box
      sx={{
        height: isLogin ? "100dvh" : "125dvh",
        backgroundColor: colourTheme.palette.background.default,
        color: colourTheme.palette.text.primary,
        backdropFilter: "blur(1rem)",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          width: "100dvw",
          height: "100dvh",
          top: "0%",
          left: "0%",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "400px",
            filter: "none",
            zIndex: 1300,
            objectFit: "contain",
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Box
            sx={{
              width: "80%",
              margin: "auto",
            }}
          >
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
              }}
            >
              {/* Logo */}
              <Logo height="5rem" width="5rem" />
            </Box>
            {/* Form Title */}
            <Box
              sx={{
                display: "grid",
                placeItems: "center",
                margin: "auto",
                fontSize: "2rem",
                mt: 5,
                mb: 2,
              }}
            >
              {isLogin ? "LOGIN" : "REGISTER"}
            </Box>
            {/* Form Body */}
            <form onSubmit={handleAuth} noValidate>
              {!isLogin && (
                <>
                  <TextField
                    type="text"
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstname(e.target.value)}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={input}
                    variant="standard"
                  />
                  <TextField
                    type="text"
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastname(e.target.value)}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={input}
                    variant="standard"
                  />
                  <TextField
                    type="text"
                    label="Phone Number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    fullWidth
                    margin="normal"
                    size="small"
                    sx={input}
                    variant="standard"
                  />
                </>
              )}
              <TextField
                type="email"
                label="Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                sx={input}
                variant="standard"
                required
              />
              <TextField
                type={showPassword ? "text" : "password"}
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
                sx={input}
                variant="standard"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={toggleShowPassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {!isLogin && (
                <TextField
                  type={showPassword ? "text" : "password"}
                  label="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  margin="normal"
                  size="small"
                  sx={input}
                  variant="standard"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Box
                component="button"
                type="submit"
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: "90%",
                  margin: "auto",
                  mt: 5,
                  mb: 3,
                  px: 4,
                  py: 1,
                  height: "50px",
                  borderRadius: "0.5rem",
                  cursor: loading ? "default" : "pointer",
                  textAlign: "center",
                  border: "none",
                  backgroundColor: success
                    ? "green"
                    : error
                    ? "red"
                    : hover
                    ? `linear-gradient(to right bottom, rgba(120, 120, 120, 0.5), rgba(120, 120, 120, 0.2))`
                    : colourTheme.palette.background.alt,
                  boxShadow: hover ? "0 0 1rem rgba(0, 0, 0, 0.2)" : "none",
                  color: success || error ? "white" : "grey",
                }}
                disabled={loading}
                onMouseEnter={() => !loading && setHover(true)}
                onMouseLeave={() => setHover(false)}
                onTouchStart={() => !loading && setHover(true)}
                onTouchEnd={() => setHover(false)}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  success || error || (isLogin ? "LOGIN" : "REGISTER")
                )}
              </Box>
            </form>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 1,
                mb: 5,
                color: colourTheme.palette.text.primary,
                cursor: "pointer",
                fontSize: "0.75rem",
              }}
              onClick={handleToggleAuthMode}
            >
              {isLogin
                ? "Don't have an account? Register here."
                : "Already have an account? Login here."}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthModal;
